/**
 * Jewelpa Custom Shop Fine Jewelry Tabs & Carousel
 * Native CSS scroll-snap with mouse drag-to-scroll, real-position arrow controls,
 * accessible keyboard tablist, and dynamic "Shop all" collection link.
 * Zero external libraries, lightweight, deferred native Web Component.
 */

if (!customElements.get('custom-product-tabs')) {
  class CustomProductTabs extends HTMLElement {
    constructor() {
      super();

      this.tabs = [];
      this.panels = [];
      this.shopAllLink = null;
      this.shopAllText = null;

      // Mouse drag state
      this.isPointerDown = false;
      this.startX = 0;
      this.initialScrollLeft = 0;
      this.hasDragged = false;
      this.activeTrack = null;

      this.scrollTimeout = null;
      this.rafId = null;

      // Bind handlers
      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKeyDown = this.onTabKeyDown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.shopAllLink = this.querySelector('[data-shop-all-link]');
      this.shopAllText = this.querySelector('[data-shop-all-text]');

      // Attach tab click and keyboard handlers
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeyDown);
      });

      // Attach carousel listeners for all panels
      this.panels.forEach((panel) => {
        const track = panel.querySelector('[data-carousel-track]');
        const prevBtn = panel.querySelector('[data-carousel-prev]');
        const nextBtn = panel.querySelector('[data-carousel-next]');

        if (track) {
          track.addEventListener('scroll', this.onScroll, { passive: true });
          track.addEventListener('pointerdown', this.onPointerDown);
          track.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              this.scrollPrev(panel);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              this.scrollNext(panel);
            }
          });
        }

        if (prevBtn) {
          prevBtn.addEventListener('click', () => this.scrollPrev(panel));
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', () => this.scrollNext(panel));
        }
      });

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      window.addEventListener('resize', this.onResize, { passive: true });

      // Shopify theme editor integration
      document.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId;
        if (!blockId) return;
        const matchingTab = this.querySelector(`[data-block-id="${blockId}"]`);
        if (matchingTab) {
          this.activateTab(matchingTab);
          matchingTab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      });

      // Initialize active panel arrow state
      requestAnimationFrame(() => this.updateActivePanelArrows());
      setTimeout(() => this.updateActivePanelArrows(), 150);
    }

    disconnectedCallback() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      window.removeEventListener('resize', this.onResize);
    }

    onTabClick(event) {
      const tab = event.currentTarget;
      this.activateTab(tab);
    }

    onTabKeyDown(event) {
      const tab = event.currentTarget;
      const index = this.tabs.indexOf(tab);
      let newIndex = -1;

      if (event.key === 'ArrowRight') {
        newIndex = (index + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        newIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        newIndex = 0;
      } else if (event.key === 'End') {
        newIndex = this.tabs.length - 1;
      }

      if (newIndex !== -1) {
        event.preventDefault();
        const newTab = this.tabs[newIndex];
        newTab.focus();
        this.activateTab(newTab);
      }
    }

    activateTab(selectedTab) {
      if (!selectedTab) return;
      const targetPanelId = selectedTab.getAttribute('aria-controls');

      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
        tab.classList.toggle('is-active', isSelected);
      });

      this.panels.forEach((panel) => {
        const isActive = panel.id === targetPanelId;
        if (isActive) {
          panel.removeAttribute('hidden');
          panel.style.display = 'block';
        } else {
          panel.setAttribute('hidden', '');
          panel.style.display = 'none';
        }
      });

      // Update the "Shop all" link href and label
      const collectionUrl = selectedTab.getAttribute('data-collection-url');
      const collectionTitle = selectedTab.getAttribute('data-collection-title');

      if (this.shopAllLink && collectionUrl) {
        this.shopAllLink.setAttribute('href', collectionUrl);
        this.shopAllLink.style.display = 'inline-flex';
        if (this.shopAllText && collectionTitle) {
          this.shopAllText.textContent = collectionTitle;
        }
      } else if (this.shopAllLink) {
        this.shopAllLink.style.display = 'none';
      }

      // Sync carousel arrow states on the newly visible panel
      requestAnimationFrame(() => this.updateActivePanelArrows());
      setTimeout(() => this.updateActivePanelArrows(), 100);
    }

    getActivePanel() {
      return this.panels.find((panel) => !panel.hasAttribute('hidden'));
    }

    updateActivePanelArrows() {
      const activePanel = this.getActivePanel();
      if (!activePanel) return;

      const track = activePanel.querySelector('[data-carousel-track]');
      const prevBtn = activePanel.querySelector('[data-carousel-prev]');
      const nextBtn = activePanel.querySelector('[data-carousel-next]');

      if (!track) return;

      const scrollLeft = track.scrollLeft;
      const scrollWidth = track.scrollWidth;
      const clientWidth = track.clientWidth;
      const maxScrollLeft = scrollWidth - clientWidth;

      // 2px tolerance for sub-pixel coordinates
      const isAtStart = scrollLeft <= 2;
      const isAtEnd = scrollLeft >= maxScrollLeft - 2;

      if (prevBtn) {
        prevBtn.disabled = isAtStart;
        prevBtn.setAttribute('aria-disabled', isAtStart ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.disabled = isAtEnd;
        nextBtn.setAttribute('aria-disabled', isAtEnd ? 'true' : 'false');
      }
    }

    getScrollStep(track) {
      if (!track) return 280;
      const card = track.querySelector('.custom-product-card');
      if (!card) return track.clientWidth * 0.8;
      const cardWidth = card.getBoundingClientRect().width;
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap) || 20;
      return cardWidth + gap;
    }

    scrollPrev(panel) {
      const track = panel.querySelector('[data-carousel-track]');
      if (!track) return;
      const step = this.getScrollStep(track);
      track.scrollBy({ left: -step, behavior: 'smooth' });
      setTimeout(() => this.updateActivePanelArrows(), 350);
    }

    scrollNext(panel) {
      const track = panel.querySelector('[data-carousel-track]');
      if (!track) return;
      const step = this.getScrollStep(track);
      track.scrollBy({ left: step, behavior: 'smooth' });
      setTimeout(() => this.updateActivePanelArrows(), 350);
    }

    onScroll() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.updateActivePanelArrows());

      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.updateActivePanelArrows(), 60);
    }

    onResize() {
      this.updateActivePanelArrows();
    }

    /**
     * Mouse drag-to-scroll implementation
     */
    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, input, select, product-form-component')) return;

      const track = event.currentTarget;
      this.isPointerDown = true;
      this.activeTrack = track;
      this.startX = event.pageX;
      this.initialScrollLeft = track.scrollLeft;
      this.hasDragged = false;
    }

    onPointerMove(event) {
      if (!this.isPointerDown || !this.activeTrack) return;

      const deltaX = event.pageX - this.startX;
      if (!this.hasDragged && Math.abs(deltaX) > 4) {
        this.hasDragged = true;
        this.activeTrack.classList.add('is-dragging');
      }

      if (this.hasDragged) {
        event.preventDefault();
        this.activeTrack.scrollLeft = this.initialScrollLeft - deltaX;
        this.updateActivePanelArrows();
      }
    }

    onPointerUp() {
      if (!this.isPointerDown) return;
      this.isPointerDown = false;

      if (this.hasDragged && this.activeTrack) {
        this.activeTrack.classList.remove('is-dragging');

        // Prevent accidental link navigation after dragging
        const captureClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        this.activeTrack.addEventListener('click', captureClick, { capture: true, once: true });
        setTimeout(() => {
          if (this.activeTrack) {
            this.activeTrack.removeEventListener('click', captureClick, { capture: true });
          }
        }, 120);

        this.updateActivePanelArrows();
      }
      this.activeTrack = null;
    }
  }

  customElements.define('custom-product-tabs', CustomProductTabs);
}
